import React, { useEffect, useRef, useState } from "react";
import { File, SpinnerGap, UploadSimple, X } from "@phosphor-icons/react";

const FileUploadField = ({
    label,
    accept,
    name,
    required = false,
    value = null,
    helperText = "PNG, JPG or WEBP up to 1 MB",
    loading = false,
    disabled = false,
    onFileSelect,
    onRemove,
}) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(typeof value === "string" ? value : null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const objectUrlRef = useRef(null);

    const clearObjectUrl = () => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    };

    useEffect(() => {
        if (!file) setPreview(typeof value === "string" && value ? value : null);
    }, [file, value]);

    useEffect(() => () => clearObjectUrl(), []);

    const handleFile = async (selectedFile) => {
        if (!selectedFile || disabled || loading) return;

        const result = onFileSelect ? await onFileSelect(selectedFile) : selectedFile;
        if (result === false) {
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const acceptedFile = result instanceof Blob ? result : selectedFile;
        clearObjectUrl();
        setFile(acceptedFile);

        if (acceptedFile.type?.startsWith("image/")) {
            objectUrlRef.current = URL.createObjectURL(acceptedFile);
            setPreview(objectUrlRef.current);
        } else {
            setPreview(null);
        }
    };

    const openFilePicker = () => {
        if (!disabled && !loading) fileInputRef.current?.click();
    };

    const onDragOver = (event) => {
        event.preventDefault();
        if (!disabled && !loading) setIsDragging(true);
    };

    const onDragLeave = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setIsDragging(false);
    };

    const onDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        handleFile(event.dataTransfer.files?.[0]);
    };

    const removeFile = (event) => {
        event.stopPropagation();
        clearObjectUrl();
        setFile(null);
        setPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (onRemove) onRemove();
        else onFileSelect?.(null);
    };

    const fileName = file?.name || (preview ? "Current event logo" : "");
    const fileSize = file?.size ? `${Math.ceil(file.size / 1024)} KB` : "Ready to use";

    return (
        <div className="event-file-upload">
            {label && (
                <label className="event-file-upload__label" htmlFor={`${name}-input`}>
                    {label}
                    {required && <span aria-hidden="true"> *</span>}
                </label>
            )}

            <div
                className={`event-file-upload__dropzone${isDragging ? " is-dragging" : ""}${
                    disabled ? " is-disabled" : ""
                }`}
                onClick={openFilePicker}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openFilePicker();
                    }
                }}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled || loading}
            >
                <input
                    id={`${name}-input`}
                    type="file"
                    ref={fileInputRef}
                    name={name}
                    onChange={(event) => handleFile(event.target.files?.[0])}
                    accept={accept}
                    disabled={disabled || loading}
                    className="sr-only"
                    tabIndex={-1}
                />

                {loading ? (
                    <div className="event-file-upload__empty" aria-live="polite">
                        <SpinnerGap size={30} weight="bold" className="event-file-upload__spinner" />
                        <strong>Preparing image...</strong>
                        <span>Optimizing your logo for upload</span>
                    </div>
                ) : preview || file ? (
                    <div className="event-file-upload__selection">
                        <div className="event-file-upload__preview">
                            {preview ? (
                                <img src={preview} alt="Event logo preview" />
                            ) : (
                                <File size={30} weight="duotone" />
                            )}
                        </div>
                        <div className="event-file-upload__meta">
                            <strong>{fileName}</strong>
                            <span>{fileSize} · Click or drop to replace</span>
                        </div>
                        <button
                            type="button"
                            className="event-file-upload__remove"
                            onClick={removeFile}
                            aria-label="Remove event logo"
                        >
                            <X size={18} weight="bold" />
                        </button>
                    </div>
                ) : (
                    <div className="event-file-upload__empty">
                        <span className="event-file-upload__icon" aria-hidden="true">
                            <UploadSimple size={30} weight="regular" />
                        </span>
                        <strong>Click or drag file here to upload</strong>
                        <span>{helperText}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FileUploadField;
